import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { paymentStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Seo } from "../components/Seo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ status: "initiated", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    let count = 0;
    const poll = async () => {
      if (stopped) return;
      if (count >= 12) return;
      try {
        const s = await paymentStatus(sessionId);
        setStatus(s);
        setAttempts(count + 1);
        if (s.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (s.payment_status === "failed" || s.payment_status === "expired") return;
      } catch {}
      count += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed" || status.payment_status === "expired";

  return (
    <div data-testid="payment-success-page" className="max-w-[820px] mx-auto px-6 lg:px-10 py-24">
      <Seo title="Order Confirmed" description="Your Peptide Labs N.I. order." noindex />
      <div className="border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] p-12 text-center">
        {paid ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#1565FF]/15 grid place-items-center text-[#1565FF]">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="mt-8 text-3xl lg:text-4xl text-white tracking-tight font-medium">
              Payment confirmed.
            </h1>
            <p className="mt-4 text-[#B7BCC7]">
              Order <span className="font-mono text-white">{sessionId?.slice(-10)}</span> confirmed.
              A receipt is on its way. Cold-chain dispatch typically ships within 24 hours.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/products" className="btn-primary inline-flex items-center gap-2">
                Continue Research <ArrowRight size={16} />
              </Link>
              <Link to="/knowledge" className="btn-ghost">Read the Knowledge Base</Link>
            </div>
          </>
        ) : failed ? (
          <>
            <h1 className="text-3xl text-white tracking-tight font-medium">Payment could not be confirmed.</h1>
            <p className="mt-4 text-[#B7BCC7]">Please try again or contact support.</p>
            <Link to="/cart" className="btn-primary inline-flex items-center gap-2 mt-8">
              Return to Cart <ArrowRight size={16} />
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto animate-spin text-[#1565FF]" size={32} />
            <h1 className="mt-6 text-2xl text-white tracking-tight font-medium">Confirming your payment…</h1>
            <p className="mt-3 text-[#6E7784] text-sm">Checking with Stripe (attempt {attempts}/12).</p>
          </>
        )}
      </div>
    </div>
  );
}
